import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../components/common/PageLayout';
import { Link } from 'react-router-dom';

const CookiePolicy: React.FC = () => {
  const { t } = useTranslation();
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;

  return (
    <>
      <Helmet>
        <title>Cookie Policy | MWQQ.com - How We Use Cookies</title>
        <meta name="description" content="Learn about our cookie usage. We use minimal cookies for essential functionality and optional analytics. Manage your preferences anytime." />
        <meta name="keywords" content="cookie policy, cookies, analytics cookies, GDPR cookies, cookie consent, privacy" />
        <link rel="canonical" href={`${baseUrl}/cookies.html`} />
        <meta property="og:title" content="Cookie Policy | MWQQ.com" />
        <meta property="og:description" content="Our cookie policy - minimal cookies for essential functionality." />
        <meta property="og:url" content={`${baseUrl}/cookies.html`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageLayout title={t('pages.cookies.title')}>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('pages.cookies.lastUpdated')}: October 1, 2025
      </p>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.cookies.sections.intro.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.cookies.sections.intro.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.cookies.sections.whatAreCookies.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.cookies.sections.whatAreCookies.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.cookies.sections.typesOfCookies.title')}
        </h2>

        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
            {t('pages.cookies.sections.typesOfCookies.essential.title')}
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            {t('pages.cookies.sections.typesOfCookies.essential.content')}
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            {t('pages.cookies.sections.typesOfCookies.essential.points', { returnObjects: true }).map((point: string, index: number) => (
              <li key={index}>{point}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
            {t('pages.cookies.sections.typesOfCookies.analytics.title')}
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            {t('pages.cookies.sections.typesOfCookies.analytics.content')}
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
            {t('pages.cookies.sections.typesOfCookies.analytics.points', { returnObjects: true }).map((point: string, index: number) => (
              <li key={index}>{point}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.cookies.sections.manageCookies.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.cookies.sections.manageCookies.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.cookies.sections.manageCookies.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.cookies.sections.thirdParty.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.cookies.sections.thirdParty.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.cookies.sections.updates.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.cookies.sections.updates.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.cookies.sections.contact.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.cookies.sections.contact.content')}
        </p>
        <a
          href="mailto:mwqq.com@gmail.com"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          mwqq.com@gmail.com
        </a>
      </section>

      <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Related:{' '}
          <Link to="/privacy.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.privacy')}
          </Link>
          {' | '}
          <Link to="/terms.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.terms')}
          </Link>
        </p>
      </div>
      </PageLayout>
    </>
  );
};

export default CookiePolicy;
